import { useContext } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { UserContext } from "../../context/UserContext";
import { UpdateNotificationPreferenceDto } from "../../types/api";
import { useSnackbar } from "../useSnackbar";
import {
  deleteUserProfilePicture,
  fetchMentionableUsers,
  fetchMyUser,
  updateNotificationPreferences,
  updateUserDetails,
  updateUserPreference,
  updateUserProfilePicture,
  updateUserState,
} from "@/lib/api";

type UseUserReturnType = {
  updateUserDetailsMutation: any;
  updateUserPreferenceMutation: any;
  updateUnitOverrideMutation: any;
  updateNotificationPreferencesMutation: any;
  mentionableUsersQuery: any;
  updateUserStateMutation: any;
  updateProfilePictureMutation: any;
  deleteProfilePictureMutation: any;
};

export const useUser = (userId: string): UseUserReturnType => {
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();
  const { t } = useTranslation();
  const { setUser } = useContext(UserContext);

  const updateUserDetailsMutation = useMutation(
    (data: { userData: any; showSuccessSnackbar?: boolean }) =>
      updateUserDetails(userId, data.userData),
    {
      onSuccess: async (_, { showSuccessSnackbar = true }) => {
        const updatedUser = await fetchMyUser();
        setUser(updatedUser);
        queryClient.invalidateQueries(["userData", userId]);
        if (showSuccessSnackbar) {
          showSnackbar(t("toast.messages.user.detailsUpdated"), "success");
        }
      },
      onError: () => {
        showSnackbar(t("toast.messages.user.detailsUpdateFailed"), "info");
      },
    }
  );

  const updateUserPreferenceMutation = useMutation(
    ({ key, value }: { key: string; value: any }) =>
      updateUserPreference(userId, key, value),
    {
      onSuccess: async () => {
        const updatedUser = await fetchMyUser();
        setUser(updatedUser);
        queryClient.invalidateQueries(["userData", userId]);
        showSnackbar(t("toast.messages.user.preferenceUpdated"), "success");
      },
      onError: () => {
        showSnackbar(t("toast.messages.user.preferenceUpdateFailed"), "info");
      },
    }
  );

  const updateUnitOverrideMutation = useMutation(
    ({ key, value }: { key: string; value: string | null }) =>
      updateUserPreference(userId, key, value),
    {
      onSuccess: async () => {
        const updatedUser = await fetchMyUser();
        setUser(updatedUser);
        queryClient.invalidateQueries(["userData", userId]);
        queryClient.invalidateQueries({
          predicate: (query) => {
            const qKey = query.queryKey as unknown[];
            return qKey[0] === "effectiveUnitPreferences";
          },
        });
        showSnackbar(t("toast.messages.user.preferenceUpdated"), "success");
      },
      onError: () => {
        showSnackbar(t("toast.messages.user.preferenceUpdateFailed"), "info");
      },
    }
  );

  const updateNotificationPreferencesMutation = useMutation(
    (preferences: UpdateNotificationPreferenceDto) =>
      updateNotificationPreferences(userId, preferences),
    {
      onSuccess: async () => {
        const updatedUser = await fetchMyUser();
        setUser(updatedUser);
        queryClient.invalidateQueries(["userData", userId]);
        showSnackbar(
          t("toast.messages.user.notificationPreferencesUpdated"),
          "success"
        );
      },
      onError: () => {
        showSnackbar(
          t("toast.messages.user.notificationPreferencesFailed"),
          "error"
        );
      },
    }
  );

  const mentionableUsersQuery = useQuery(
    ["mentionableUsers"],
    fetchMentionableUsers,
    {
      enabled: false,
      onError: () => {
        showSnackbar(t("toast.messages.user.mentionableUsersFailed"), "error");
      },
    }
  );

  const fetchMentionableUsersIfNeeded = () => {
    if (!mentionableUsersQuery.data) {
      mentionableUsersQuery.refetch();
    }
  };

  const updateUserStateMutation = useMutation(
    ({ stateName, value }: { stateName: string; value: boolean }) =>
      updateUserState(userId, { stateName, value }),
    {
      onSuccess: async () => {
        const updatedUser = await fetchMyUser();
        setUser(updatedUser);
      },
      onError: (error) => {
        console.error("Error updating user state:", error);
        showSnackbar(t("toast.messages.user.stateUpdateFailed"), "info");
      },
    }
  );

  const updateProfilePictureMutation = useMutation(
    (profilePicture: File) => updateUserProfilePicture(userId, profilePicture),
    {
      onSuccess: async () => {
        showSnackbar(t("toast.messages.user.profilePictureChanged"), "success");
        const updatedUser = await fetchMyUser();
        setUser(updatedUser);
      },
      onError: (error) => {
        console.log("error", error);
        showSnackbar(
          t("toast.messages.user.profilePictureChangeFailed"),
          "error"
        );
      },
    }
  );

  const deleteProfilePictureMutation = useMutation(
    () => deleteUserProfilePicture(userId),
    {
      onSuccess: async () => {
        showSnackbar(t("toast.messages.user.profilePictureRemoved"), "success");
        const updatedUser = await fetchMyUser();
        setUser(updatedUser);
      },
      onError: (error) => {
        console.log("error", error);
        showSnackbar(
          t("toast.messages.user.profilePictureRemoveFailed"),
          "error"
        );
      },
    }
  );

  return {
    updateUserDetailsMutation,
    updateUserPreferenceMutation,
    updateUnitOverrideMutation,
    updateNotificationPreferencesMutation,
    mentionableUsersQuery: {
      data: mentionableUsersQuery.data,
      isLoading: mentionableUsersQuery.isLoading,
      isError: mentionableUsersQuery.isError,
      fetchIfNeeded: fetchMentionableUsersIfNeeded,
    },
    updateUserStateMutation,
    updateProfilePictureMutation,
    deleteProfilePictureMutation,
  };
};
